import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { BarTitle } from "../components/CollapseBar";
import { useBranches } from "../components/TirPlanView";
import { Scale, ExternalLink } from "lucide-react";

const tl = (n) => (Number(n) || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 });
const inp = "h-7 rounded border border-slate-700 bg-slate-800 px-2 text-xs text-slate-100";
const today = () => new Date().toISOString().slice(0, 10);
const td = "border-b border-slate-800 px-2 py-0.5 whitespace-nowrap";

function rangeOf(mode, day, month, from, to) {
  if (mode === "GUN") return [day, day];
  if (mode === "AY") { const [y, m] = month.split("-").map(Number); return [`${month}-01`, `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`]; }
  if (mode === "ARALIK") return [from, to];
  return ["", ""];
}

function Row({ r, onSaved, open }) {
  const [f, setF] = useState(r);
  useEffect(() => setF(r), [r]);
  const save = async (k, v) => { if (String(v) === String(r[k] ?? "")) return; await api.put(`/tirs/${r.id}/meta`, { data: { [k]: v } }); onSaved(); };
  const cell = (k, w = "w-28", type = "text") => <input data-testid={`fin-row-${k}-${r.id}`} type={type} list={type === "text" && k.endsWith("sube") ? "mw-branches" : undefined} value={f[k] ?? ""} onChange={(e) => setF((o) => ({ ...o, [k]: e.target.value }))} onBlur={(e) => save(k, e.target.value)} className={`${inp} ${w}`} />;
  return (
    <tr data-testid={`fin-row-${r.id}`} className="hover:bg-slate-800/40">
      <td className={td}>{cell("tarih", "w-32", "date")}</td>
      <td className={td}><button data-testid={`fin-row-open-${r.id}`} onClick={open} className="flex items-center gap-1 font-semibold text-sky-300 hover:underline">{r.plate || r.id} <ExternalLink className="h-3 w-3" /></button></td>
      <td className={td}>{cell("cikis_sube")}</td>
      <td className={td}>{cell("varis_sube")}</td>
      <td className={td}>
        <select data-testid={`fin-row-odeyen-${r.id}`} value={f.navlun_odeyen || ""} onChange={(e) => save("navlun_odeyen", e.target.value)} className={inp}>
          {[...new Set([r.cikis_sube, r.varis_sube, r.navlun_odeyen].filter(Boolean))].map((x) => <option key={x}>{x}</option>)}
        </select>
      </td>
      <td className={td}>{cell("navlun", "w-24")}</td>
      <td className={td}>{cell("ciro", "w-24")}</td>
      <td className={`${td} font-bold ${r.net >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{tl(r.net)}</td>
      <td className={td}>{tl(r.hakedis_cikis)}</td>
      <td className={td}>{tl(r.hakedis_varis)}</td>
    </tr>
  );
}

export default function NavlunHakedis() {
  const nav = useNavigate();
  const branches = useBranches();
  const [mode, setMode] = useState("AY");
  const [day, setDay] = useState(today());
  const [month, setMonth] = useState(today().slice(0, 7));
  const [from, setFrom] = useState(today().slice(0, 8) + "01");
  const [to, setTo] = useState(today());
  const [d, setD] = useState({ rows: [], branches: [], total: {} });
  const load = useCallback(() => {
    const [start, end] = rangeOf(mode, day, month, from, to);
    api.get("/tir-finance", { params: { start, end }, silent: true }).then((r) => setD(r.data));
  }, [mode, day, month, from, to]);
  useEffect(() => { load(); }, [load]);
  const t = d.total || {};
  return (
    <div className="mw-fade-up space-y-3" data-testid="navlun-hakedis-page">
      <BarTitle><div>
        <h1 className="font-head flex items-center gap-2 text-3xl font-bold tracking-tight text-white"><Scale className="h-7 w-7 text-amber-400" /> NAVLUN & HAK EDİŞ</h1>
        <p className="text-sm text-slate-400">HAK EDİŞ = (CİRO − NAVLUN) ÷ 2 → YARISI ÇIKIŞ, YARISI VARIŞ ŞUBESİNE</p>
      </div></BarTitle>
      <datalist id="mw-branches">{branches.map((b) => <option key={b} value={b} />)}</datalist>
      <div data-testid="fin-filters" className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-800 bg-slate-900 p-2 text-xs text-slate-300">
        {[["GUN", "GÜNLÜK"], ["AY", "AYLIK"], ["ARALIK", "TARİH ARALIĞI"], ["HEPSI", "TÜMÜ"]].map(([k, l]) => (
          <button key={k} data-testid={`fin-mode-${k}`} onClick={() => setMode(k)} className={`rounded px-2 py-1 font-semibold ${mode === k ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300"}`}>{l}</button>
        ))}
        {mode === "GUN" && <input data-testid="fin-day" type="date" value={day} onChange={(e) => setDay(e.target.value)} className={inp} />}
        {mode === "AY" && <input data-testid="fin-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inp} />}
        {mode === "ARALIK" && <><input data-testid="fin-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inp} /> — <input data-testid="fin-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inp} /></>}
        <span className="ml-auto" data-testid="fin-totals">{d.rows.length} TIR · CİRO <b className="text-white">{tl(t.ciro)}</b> · NAVLUN <b className="text-white">{tl(t.navlun)}</b> · {t.net >= 0 ? "KÂR" : "ZARAR"} <b className={t.net >= 0 ? "text-emerald-300" : "text-rose-300"}>{tl(t.net)}</b></span>
      </div>
      <div className="overflow-auto rounded-lg border border-slate-800 bg-slate-900" style={{ maxHeight: "55vh" }}>
        <table data-testid="fin-table" className="w-full text-xs text-slate-200">
          <thead className="sticky top-0 bg-slate-800 text-left text-[10px] text-slate-400"><tr>
            {["TARİH", "TIR / PLAKA", "ÇIKIŞ ŞUBE", "VARIŞ ŞUBE", "NAVLUNU ÖDEYEN", "NAVLUN", "CİRO", "KÂR / ZARAR", "HAK EDİŞ ÇIKIŞ", "HAK EDİŞ VARIŞ"].map((h) => <th key={h} className="px-2 py-1">{h}</th>)}
          </tr></thead>
          <tbody>
            {d.rows.map((r) => <Row key={r.id} r={r} onSaved={load} open={() => nav(`/tir-plan/${r.id}`)} />)}
            {!d.rows.length && <tr><td colSpan={10} className="px-2 py-8 text-center text-slate-500">BU TARİHTE TIR YOK</td></tr>}
          </tbody>
        </table>
      </div>
      <h2 className="text-base font-bold text-white md:text-lg">ŞUBE HAK EDİŞLERİ</h2>
      <div data-testid="fin-branches" className="overflow-auto rounded-lg border border-slate-800 bg-slate-900">
        <table className="w-full text-xs text-slate-200">
          <thead className="bg-slate-800 text-left text-[10px] text-slate-400"><tr>
            {["ŞUBE", "TIR", "ÇIKIŞ CİROSU", "VARIŞ CİROSU", "ÖDEDİĞİ NAVLUN (HANESİ)", "HAK EDİŞ"].map((h) => <th key={h} className="px-2 py-1">{h}</th>)}
          </tr></thead>
          <tbody>
            {d.branches.map((b) => (
              <tr key={b.sube} data-testid={`fin-branch-${b.sube}`}>
                <td className={`${td} font-bold text-white`}>{b.sube}</td><td className={td}>{b.tir}</td>
                <td className={td}>{tl(b.cikis_ciro)}</td><td className={td}>{tl(b.varis_ciro)}</td>
                <td className={td}>{tl(b.navlun)}</td><td className={`${td} font-bold text-emerald-300`}>{tl(b.hakedis)}</td>
              </tr>
            ))}
            <tr className="bg-slate-800/60 font-bold"><td className={td}>TOPLAM</td><td className={td}>{d.rows.length}</td><td className={td}>{tl(t.ciro)}</td><td className={td}>{tl(t.ciro)}</td><td className={td}>{tl(t.navlun)}</td><td className={`${td} text-emerald-300`}>{tl(t.net)}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
